import React, { useState, useEffect } from 'react';
import { API_BASE, apiHeaders, apiPost, leseFallback, zeigeApiFehler } from '../../lib/api';
import { schuljahrKurz } from '../../lib/schuljahr';
import { uebergangApi, SchuelerMin } from '../../lib/uebergang-api';
import { toast } from '../../lib/toast';
import { MIcon } from '../../lib/micon';

interface InaktiverSchueler {
  id: number;
  vorname: string;
  nachname: string;
  klasse_name: string | null;
  verlassen_am: string | null;
}

interface ArchivKlasse {
  id: number;
  name: string;
  schuljahr: string;
  status: 'archiviert' | 'aufgeloest';
  archiviert_am: string | null;
  schueler_count: number;
  nachfolger_name: string | null;
  nachfolger_id: number | null;
}

// ── Drill-Down Detail-View ────────────────────────────────────────────────────

function KlasseDetail({ klasse, onZurueck }: { klasse: ArchivKlasse; onZurueck: () => void }) {
  const [schueler, setSchueler] = useState<SchuelerMin[]>([]);
  const [laden, setLaden] = useState(true);

  useEffect(() => {
    uebergangApi.klasseSchueler(klasse.id)
      .then(setSchueler)
      .catch(() => {})
      .finally(() => setLaden(false));
  }, [klasse.id]);

  const archDatum = klasse.archiviert_am
    ? new Date(klasse.archiviert_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      {/* Header-Banner */}
      <div style={{
        background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: 'var(--ld-radius-lg)',
        padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <MIcon name="inventory_2" size={20} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ld-text-secondary)' }}>
            ARCHIV-ANSICHT · NUR LESEN
          </div>
          <div style={{ fontSize: 13, color: 'var(--ld-text-secondary)' }}>
            {klasse.status === 'aufgeloest' ? 'Aufgelöst' : 'Archiviert'} am {archDatum} · Schuljahr {schuljahrKurz(klasse.schuljahr)}
          </div>
        </div>
      </div>

      <button onClick={onZurueck} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
        fontSize: 14, color: 'var(--ld-primary)', fontWeight: 600, marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        ← Zurück zum Archiv
      </button>

      <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px', color: 'var(--ld-text-primary)' }}>
        {klasse.name}
      </h2>
      <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: '0 0 24px' }}>
        Schuljahr {schuljahrKurz(klasse.schuljahr)}
        {klasse.nachfolger_name && ` · Promoviert zu ${klasse.nachfolger_name}`}
      </p>

      {/* Nachfolger-Info */}
      {klasse.nachfolger_name && (
        <div style={{
          background: '#EDE9F8', borderRadius: 'var(--ld-radius-md)', padding: '12px 16px', marginBottom: 20,
          fontSize: 13, color: 'var(--ld-primary)', fontWeight: 600,
        }}>
          {klasse.schueler_count === 0
            ? `Alle Schüler/innen wurden in Klasse ${klasse.nachfolger_name} übernommen.`
            : `Teilweise Schüler/innen übernommen → ${klasse.nachfolger_name}`
          }
        </div>
      )}

      {/* Schülerliste */}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ld-text-secondary)', marginBottom: 8 }}>
        {klasse.nachfolger_name ? 'Nicht übernommene Schüler/innen' : 'Schüler/innen'}
      </div>

      {laden ? (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--ld-text-secondary)', fontSize: 13 }}>
          Laden …
        </div>
      ) : schueler.length === 0 ? (
        <div style={{
          background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-md)', padding: '20px 16px',
          textAlign: 'center', color: 'var(--ld-text-secondary)', fontSize: 13,
        }}>
          {klasse.nachfolger_name
            ? 'Alle Schüler/innen wurden übernommen — keine verbleibenden Einträge.'
            : 'Keine Schüler/innen in dieser Klasse erfasst.'
          }
        </div>
      ) : (
        <div style={{ border: '1.5px solid var(--ld-outline-soft)', borderRadius: 'var(--ld-radius-md)', overflow: 'hidden' }}>
          {schueler.map((s, i) => (
            <div key={s.id} style={{
              padding: '11px 16px', fontSize: 14, color: 'var(--ld-text-secondary)',
              borderBottom: i < schueler.length - 1 ? '1px solid #F0F0F5' : 'none',
              background: 'var(--ld-bg-card)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <MIcon name="person" size={16} color="var(--ld-text-tertiary)" />
              {s.nachname}, {s.vorname}
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>read-only</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Haupt-Archiv-Screen ───────────────────────────────────────────────────────

export function ArchivScreen() {
  const [klassen, setKlassen] = useState<ArchivKlasse[]>([]);
  const [laden, setLaden]     = useState(true);
  const [detail, setDetail]   = useState<ArchivKlasse | null>(null);
  const [inaktive, setInaktive] = useState<InaktiverSchueler[]>([]);

  function ladeInaktive() {
    fetch(`${API_BASE}/api/schueler/inaktiv`, { headers: apiHeaders() })
      .then(leseFallback<{ schueler: InaktiverSchueler[] }>({ schueler: [] }, '/api/schueler/inaktiv'))
      .then(d => setInaktive(d.schueler ?? []))
      .catch(() => {});
  }
  useEffect(() => { ladeInaktive(); }, []);

  async function reaktiviere(id: number, name: string) {
    try {
      await apiPost(`/api/schueler/${id}/reaktivieren`);
      setInaktive(prev => prev.filter(s => s.id !== id));
      toast(`${name} wurde reaktiviert`, 'success');
      window.dispatchEvent(new Event('lehrerdesk-schueler-reload'));
      window.dispatchEvent(new CustomEvent('lehrerdesk-klassen-reload'));
    } catch (e) { zeigeApiFehler(e, 'Reaktivierung fehlgeschlagen'); }
  }

  useEffect(() => {
    fetch(`${API_BASE}/api/uebergang/archiv`, { headers: apiHeaders() })
      .then(leseFallback<{ archivierte_klassen: ArchivKlasse[] }>({ archivierte_klassen: [] }, '/api/uebergang/archiv'))
      .then(d => setKlassen(d.archivierte_klassen ?? []))
      .catch(() => {})
      .finally(() => setLaden(false));
  }, []);

  if (detail) {
    return <KlasseDetail klasse={detail} onZurueck={() => setDetail(null)} />;
  }

  // Klassen nach Schuljahr gruppieren
  const gruppen = klassen.reduce<Record<string, ArchivKlasse[]>>((acc, k) => {
    (acc[k.schuljahr] ??= []).push(k);
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 2px', color: 'var(--ld-text-primary)' }}>
        Archiv
      </h1>
      <p style={{ fontSize: 14, color: 'var(--ld-text-secondary)', margin: '0 0 24px' }}>
        Archivierte und aufgelöste Klassen, nur zum Ansehen.
      </p>

      {/* Inaktive (verlassene) Schüler */}
      {inaktive.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.5, marginBottom: 10 }}>
            Inaktive Schüler/innen ({inaktive.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {inaktive.map(s => (
              <div key={s.id} style={{
                background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-lg)', border: '1.5px solid var(--ld-outline-soft)',
                padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, boxShadow: 'var(--ld-shadow-card)',
              }}>
                <MIcon name="person" size={16} color="var(--ld-text-tertiary)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ld-text-primary)' }}>{s.nachname}, {s.vorname}</div>
                  <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)' }}>
                    {s.klasse_name ?? '–'}
                    {s.verlassen_am && ` · verlassen am ${new Date(s.verlassen_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}`}
                  </div>
                </div>
                <button onClick={() => reaktiviere(s.id, `${s.vorname} ${s.nachname}`)} style={{
                  padding: '7px 13px', borderRadius: 'var(--ld-radius-md)', border: '1.5px solid var(--ld-primary)',
                  background: 'transparent', color: 'var(--ld-primary)', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  Reaktivieren
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {laden ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ld-text-secondary)' }}>
          <span style={{ width: 24, height: 24, border: '2px solid var(--ld-primary)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} />
        </div>
      ) : klassen.length === 0 ? (
        <div style={{
          background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-xl)', padding: '40px 24px',
          textAlign: 'center', color: 'var(--ld-text-secondary)', fontSize: 14,
          boxShadow: 'var(--ld-shadow-card)',
        }}>
          Noch keine archivierten Klassen.
        </div>
      ) : (
        Object.entries(gruppen).sort(([a], [b]) => b.localeCompare(a)).map(([sj, ks]) => (
          <div key={sj} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.5, marginBottom: 10 }}>
              SCHULJAHR {sj}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ks.map(k => (
                <button key={k.id} onClick={() => setDetail(k)} style={{
                  textAlign: 'left', background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-lg)',
                  border: '1.5px solid var(--ld-outline-soft)', padding: '14px 16px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 12,
                  boxShadow: 'var(--ld-shadow-card)',
                  transition: 'border-color .15s',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ld-text-primary)', marginBottom: 2 }}>
                      {k.name}
                      <span style={{
                        marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                        background: k.status === 'aufgeloest' ? '#FEE2E2' : '#F0F9FF',
                        color: k.status === 'aufgeloest' ? '#DC2626' : '#0369A1',
                      }}>
                        {k.status === 'aufgeloest' ? 'aufgelöst' : 'archiviert'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)' }}>
                      {k.schueler_count} Schüler/innen verbleibend
                      {k.nachfolger_name && ` · → ${k.nachfolger_name}`}
                      {k.archiviert_am && ` · ${new Date(k.archiviert_am).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}`}
                    </div>
                  </div>
                  <span style={{ color: 'var(--ld-text-secondary)', fontSize: 18 }}>›</span>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
